import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Calendar, 
  Users, 
  CheckCircle2, 
  Clock, 
  Share2, 
  Globe,
  ArrowRight,
  Star,
  Shield,
  Heart,
  Sparkles,
  CalendarPlus,
  CalendarCheck,
  Eye,
  Send,
  PartyPopper,
  Download,
  FileText,
  Loader2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarGuidePage = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/calendar/guide-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CCA_Calendar_Booking_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Guide downloaded! Share it with your community.');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleSharePDFWhatsApp = async () => {
    // First download the PDF, then prompt user to share
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/calendar/guide-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CCA_Calendar_Booking_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // After download, open WhatsApp with message
      setTimeout(() => {
        const message = encodeURIComponent(
          `📅 CCA Calendar Booking Guide\n\n` +
          `Learn how to book your cultural events on the CCA Montreal Calendar!\n\n` +
          `📖 View online: ${window.location.href}\n\n` +
          `I've also attached the PDF guide for offline viewing.\n\n` +
          `#CCAMontreal #CameroonianCulture`
        );
        window.open(`https://wa.me/?text=${message}`, '_blank');
        toast.success('PDF downloaded! Share it on WhatsApp along with the message.');
      }, 500);
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to prepare PDF for sharing');
    } finally {
      setDownloading(false);
    }
  };

  const steps = [
    {
      number: "01",
      title: "Visit the CCA Calendar",
      description: "Navigate to the CCA Cultural Calendar to view all scheduled events and available dates for your community activities.",
      icon: Calendar,
      color: "cameroon-green",
      bgGradient: "from-cameroon-green/20 to-cameroon-green/5",
      tip: "Look for dates highlighted in green - they're still available!"
    },
    {
      number: "02",
      title: "Select Your Date",
      description: "Choose an available date that works for your event. Multi-day events like conventions and festivals are supported!",
      icon: CalendarPlus,
      color: "cameroon-red",
      bgGradient: "from-cameroon-red/20 to-cameroon-red/5",
      tip: "Plan ahead! Popular dates get booked quickly."
    },
    {
      number: "03",
      title: "Fill Event Details",
      description: "Provide your event information including title, description, time, location, and upload an eye-catching image.",
      icon: FileText,
      color: "cameroon-yellow",
      bgGradient: "from-cameroon-yellow/20 to-cameroon-yellow/5",
      tip: "A great image makes your event stand out when shared!"
    },
    {
      number: "04",
      title: "Submit for Review",
      description: "Your booking request is sent to CCA administrators for approval. You'll receive a confirmation number immediately.",
      icon: Send,
      color: "cameroon-green",
      bgGradient: "from-cameroon-green/20 to-cameroon-green/5",
      tip: "Save your confirmation number to track your booking status."
    },
    {
      number: "05",
      title: "Get Approved & Go Live",
      description: "Once approved, your event appears on the public calendar with a unique shareable link for social media!",
      icon: PartyPopper,
      color: "cameroon-red",
      bgGradient: "from-cameroon-red/20 to-cameroon-red/5",
      tip: "Share your event link with your community and watch attendance grow!"
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Avoid Double Bookings",
      description: "The calendar prevents scheduling conflicts, ensuring your event gets the spotlight it deserves without competing with others.",
      color: "cameroon-green"
    },
    {
      icon: Heart,
      title: "Community Togetherness",
      description: "By coordinating events, we encourage members to attend more activities and strengthen bonds across all associations.",
      color: "cameroon-red"
    },
    {
      icon: Globe,
      title: "Public Visibility",
      description: "Your event is showcased to the entire community and beyond, helping attract attendees and celebrate our culture.",
      color: "cameroon-yellow"
    },
    {
      icon: Share2,
      title: "Easy Social Sharing",
      description: "Each event gets a unique link with beautiful preview images, perfect for sharing on Facebook, WhatsApp, and more.",
      color: "cameroon-green"
    },
    {
      icon: Users,
      title: "Inclusive Access",
      description: "Open to all 13 CCA associations, external organizations, and individuals who want to share cultural events.",
      color: "cameroon-red"
    },
    {
      icon: Star,
      title: "Professional Presentation",
      description: "Your events are displayed beautifully with all details, making a great impression on potential attendees.",
      color: "cameroon-yellow"
    }
  ];

  const whoCanBook = [
    {
      title: "CCA Member Associations",
      description: "All 13 member associations can book dates for their cultural events, meetings, and celebrations.",
      icon: Users,
      examples: ["Annual General Meetings", "Cultural Festivals", "Community Celebrations"]
    },
    {
      title: "External Organizations",
      description: "Non-CCA cultural organizations and community groups are welcome to book and share their events.",
      icon: Globe,
      examples: ["Partner Organizations", "Cultural Groups", "Community Initiatives"]
    },
    {
      title: "Individuals",
      description: "Community members can book dates for personal cultural events and gatherings.",
      icon: Star,
      examples: ["Birthday Celebrations", "Memorial Services", "Family Gatherings"]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/95 to-cameroon-green/20"></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-cameroon-green/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cameroon-red/10 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-20 w-48 h-48 bg-cameroon-yellow/10 rounded-full blur-2xl"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cameroon-green/20 border border-cameroon-green/30 mb-8">
            <Sparkles className="w-5 h-5 text-cameroon-yellow" />
            <span className="text-sm font-medium text-white">CCA Event Booking Service</span>
          </div>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Your Guide to the
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cameroon-green via-cameroon-red to-cameroon-yellow">
              CCA Cultural Calendar
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-10">
            A comprehensive booking service that helps Cameroonian associations and community members 
            plan, coordinate, and showcase their cultural events to the public.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/calendar'}
              className="bg-gradient-to-r from-cameroon-green to-cameroon-green/80 hover:from-cameroon-green/90 hover:to-cameroon-green/70 text-white shadow-lg shadow-cameroon-green/25"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book an Event Now
            </Button>
            <Button 
              size="lg"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-gradient-to-r from-cameroon-red to-cameroon-red/80 hover:from-cameroon-red/90 hover:to-cameroon-red/70 text-white shadow-lg shadow-cameroon-red/25"
              data-testid="download-guide-pdf"
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Download className="w-5 h-5 mr-2" />
              )}
              {downloading ? 'Downloading...' : 'Download PDF Guide'}
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn How It Works
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Use the CCA Calendar?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Our calendar booking service brings the community together while ensuring every event gets the attention it deserves.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="glass border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 group"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-${benefit.color}/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <benefit.icon className={`w-7 h-7 text-${benefit.color}`} />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-white/60">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-800/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cameroon-red/20 border border-cameroon-red/30 mb-6">
              <CalendarCheck className="w-5 h-5 text-cameroon-red" />
              <span className="text-sm font-medium text-white">Step-by-Step Guide</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              How to Book Your Event
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Follow these simple steps to reserve your date on the CCA Cultural Calendar
            </p>
          </div>
          
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`relative flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                {/* Step Card */}
                <Card className={`flex-1 glass border-white/10 bg-gradient-to-br ${step.bgGradient} overflow-hidden`}>
                  <CardContent className="p-8 relative">
                    {/* Large Step Number Background */}
                    <div className={`absolute -top-4 -right-4 text-[120px] font-bold text-${step.color}/10 leading-none pointer-events-none`}>
                      {step.number}
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-16 h-16 rounded-2xl bg-${step.color}/20 border border-${step.color}/30 flex items-center justify-center`}>
                          <step.icon className={`w-8 h-8 text-${step.color}`} />
                        </div>
                        <div>
                          <span className={`text-sm font-bold text-${step.color} uppercase tracking-wider`}>Step {step.number}</span>
                          <h3 className="font-serif text-2xl font-bold text-white">{step.title}</h3>
                        </div>
                      </div>
                      
                      <p className="text-white/70 text-lg mb-4">{step.description}</p>
                      
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-${step.color}/10 border border-${step.color}/20`}>
                        <Sparkles className={`w-4 h-4 text-${step.color}`} />
                        <span className="text-sm text-white/80"><strong>Pro Tip:</strong> {step.tip}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Connector Line (visible on lg screens) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute left-1/2 -bottom-8 transform -translate-x-1/2">
                    <div className="w-1 h-16 bg-gradient-to-b from-white/20 to-transparent"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Book Section */}
      <section className="py-20 bg-gradient-to-b from-slate-800/30 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Who Can Book Events?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The CCA Calendar is open to everyone in our community
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {whoCanBook.map((category, index) => (
              <Card key={index} className="glass border-white/10 hover:border-cameroon-green/30 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cameroon-green/20 to-cameroon-red/20 flex items-center justify-center mb-6">
                    <category.icon className="w-8 h-8 text-cameroon-yellow" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-white mb-3">{category.title}</h3>
                  <p className="text-white/60 mb-6">{category.description}</p>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-cameroon-green">Examples:</p>
                    {category.examples.map((example, i) => (
                      <div key={i} className="flex items-center gap-2 text-white/70 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-cameroon-green flex-shrink-0" />
                        {example}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Share With Community Section */}
      <section className="py-20 bg-gradient-to-br from-cameroon-green/10 via-slate-900 to-cameroon-red/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="glass border-white/20 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cameroon-yellow/20 border border-cameroon-yellow/30 mb-6">
                    <Share2 className="w-4 h-4 text-cameroon-yellow" />
                    <span className="text-xs font-semibold text-cameroon-yellow uppercase">Share This Guide</span>
                  </div>
                  
                  <h2 className="font-serif text-3xl font-bold text-white mb-4">
                    Spread the Word to Your Community
                  </h2>
                  <p className="text-white/70 mb-8">
                    Help your fellow association members understand and use the CCA Calendar. 
                    Share this guide on your community WhatsApp groups, Facebook pages, and during meetings!
                  </p>
                  
                  <div className="space-y-4">
                    <Button 
                      className="w-full sm:w-auto bg-cameroon-red hover:bg-cameroon-red/90 text-white"
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      data-testid="download-pdf-share"
                    >
                      {downloading ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5 mr-2" />
                      )}
                      Download PDF to Share
                    </Button>
                    <Button 
                      className="w-full sm:w-auto bg-[#25D366] hover:bg-[#25D366]/90 text-white"
                      onClick={handleSharePDFWhatsApp}
                      disabled={downloading}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Download & Share on WhatsApp
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                      onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Share Page on Facebook
                    </Button>
                  </div>
                </div>
                
                <div className="relative hidden lg:block">
                  <div className="absolute inset-0 bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow opacity-20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
                        <Users className="w-12 h-12 text-white" />
                      </div>
                      <p className="text-white font-serif text-xl font-semibold">13 Associations</p>
                      <p className="text-white/70">United in Culture</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-8">
            <PartyPopper className="w-5 h-5 text-cameroon-yellow" />
            <span className="text-sm font-medium text-white">Ready to Get Started?</span>
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6">
            Book Your Event Today!
          </h2>
          <p className="text-white/60 mb-10 max-w-2xl mx-auto">
            Join the 13 CCA associations and community members in celebrating our rich Cameroonian culture. 
            Your event deserves to be seen!
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/calendar'}
              className="bg-gradient-to-r from-cameroon-green via-cameroon-red to-cameroon-yellow text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Go to Calendar & Book Now
            </Button>
            <Link to="/">
              <Button 
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10"
              >
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                <span className="text-white font-serif font-bold text-lg">C</span>
              </div>
              <span className="font-serif font-bold text-white">CCA Montreal</span>
            </div>
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} Council of Cultural Associations. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CalendarGuidePage;
